import { helpWhy } from '../../help/why';
import React, { useState } from 'react';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, TextField } from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { UdRegisterService } from '../../services/udRegister.service';
import { ICreateUdUtilizationRequest, IUdEntitlementLine } from '../../interfaces/commercial.interface';
import { enumLabel } from '../../utils/garmentsLabels';
import { toLocalIsoDate } from '../../utils/dateUtils';

interface Props {
    institutionId: string;
    udId: string;
    line: IUdEntitlementLine;
    onClose: () => void;
    onSaved: () => void;
}

const today = () => toLocalIsoDate();

/** Records a utilization against one entitlement line. Only reachable from
 * UdEntitlementLinePanel while the UD is APPROVED (the "record utilization" button is disabled
 * otherwise) -- the server re-enforces the same rule (§5.3 N9c). */
const UdUtilizationDialog: React.FC<Props> = ({ institutionId, udId, line, onClose, onSaved }) => {
    const toast = useToast();
    const [form, setForm] = useState<ICreateUdUtilizationRequest>({ utilizationDate: today(), qty: 0, reference: '', remarks: '' });
    const [saving, setSaving] = useState(false);

    const valid = form.qty >= 0.001 && !!form.utilizationDate;

    const saveWhy = saving ? null
        : form.qty < 0.001 ? 'Enter a quantity of at least 0.001.'
        : !form.utilizationDate ? 'Enter the utilization date.'
        : null;

    const handleSave = async () => {
        try {
            setSaving(true);
            await UdRegisterService.createUtilization(institutionId, udId, line.id, form);
            toast.showSuccess('Utilization recorded');
            onSaved();
        } catch (e) {
            toast.showApiError(e, 'Failed to record utilization');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ 'data-help-page': 'commercial.udUtilization.page' } as any}>
            <DialogTitle>Record Utilization — {enumLabel(line.materialType)}</DialogTitle>
            <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }} data-help-section="commercial.udUtilization.section.details">
                <TextField data-help="commercial.udUtilization.field.date" label="Utilization Date" type="date" InputLabelProps={{ shrink: true }} required
                    value={form.utilizationDate} onChange={e => setForm({ ...form, utilizationDate: e.target.value })} />
                <TextField data-help="commercial.udUtilization.field.qty" label={`Qty (${line.uom}) — balance ${line.balanceQty}`} type="number" required
                    value={form.qty} onChange={e => setForm({ ...form, qty: Number(e.target.value) })} />
                <TextField data-help="commercial.udUtilization.field.reference" label="Reference" value={form.reference ?? ''}
                    onChange={e => setForm({ ...form, reference: e.target.value })} />
                <TextField data-help="commercial.udUtilization.field.remarks" label="Remarks" multiline minRows={2} value={form.remarks ?? ''}
                    onChange={e => setForm({ ...form, remarks: e.target.value })} />
            </DialogContent>
            <DialogActions>
                <Button data-help="commercial.udUtilization.cancel" onClick={onClose}>Cancel</Button>
                <Button data-help="commercial.udUtilization.save" {...helpWhy(saveWhy ? { kind: 'status', message: saveWhy } : null)} variant="contained" disabled={saving || !valid} onClick={handleSave}>Save</Button>
            </DialogActions>
        </Dialog>
    );
};

export default UdUtilizationDialog;
